import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { isNullOrUndefined } from '@swimlane/ngx-datatable';
import { Subject, of, EMPTY, Observable } from 'rxjs';
import { filter, map, takeUntil, distinctUntilChanged, switchMap, take, debounceTime } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { CommonService } from 'src/app/core/services/common.service';
import { RouteService } from 'src/app/core/services/route.service';
import { StorageService } from 'src/app/core/services/storage.service';
import {
  ReturnToSuperAdmin,
} from 'src/app/core/state/_actions/settings/application-users.actions';
import { LoadCSSTheme } from 'src/app/core/state/_actions/settings/custom-theme.action';
import { LoadDefaultPageForTable } from 'src/app/core/state/_actions/settings/pages.action';
import { LoadNavigation } from 'src/app/core/state/_actions/table.actions';
import { ApplicationData } from 'src/app/core/state/_models/application.models';
import { Item, Navigation } from 'src/app/core/state/_models/table.models';
import {
  getApplicationById,
  getApplicationData
} from 'src/app/core/state/_selectors/application.selector';
import { getThemeDetail } from 'src/app/core/state/_selectors/settings/custom-theme.selector';
import { getDefaultPageForTable } from 'src/app/core/state/_selectors/settings/pages.selector';
import { getNavigation } from 'src/app/core/state/_selectors/table.selector';
import { getUserProfile } from 'src/app/core/state/_selectors/user.selector';
import { FieldService } from 'src/app/core/state/_services/field.service';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';
import { SweetAlertOptions } from 'sweetalert2';
import { alertPresets, AlertService } from '../../services/alert.service';
import Parser from '../../services/parser/parser';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { CompanyService } from 'src/app/core/state/_services/company.service';
import { ApplicationService } from 'src/app/core/state/_services/application.service';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { LDFlagValue } from 'launchdarkly-js-client-sdk';

@Component({
  selector: 'app-new-sidebar',
  templateUrl: './new-sidebar.component.html',
  styleUrls: ['./new-sidebar.component.scss'],
})
export class NewSidebarComponent implements OnInit {
  @Output() toggleEvent = new EventEmitter<boolean>();
  excludeApplicationTitles: Array<string> = ['Audit Log', 'Notification Audit Log'];
  navigation: Array<Navigation> = [];
  applicationData: ApplicationData = <ApplicationData>{};
  sidebarDesktop: boolean = false;
  sidebarMobile: boolean = false;
  isImpersonate: string | null = this.storage.getImpersonateId();
  logoSrc: string | null = '';
  icon: string | null = '';
  iconColor: string | null = '';
  routeLink: string = '/';
  isBillingAdmin: boolean = false;
  isSuperAdmin: boolean = false;
  isAuditLogEnable: boolean = false;
  isNotificationLogEnable: boolean = false;
  appName: string = 'App Builder';
  tableId: any;
  isDevMode = !environment.production;
  applicationId: any;
  isPortal: boolean = false;
  isAdmin: boolean = false;
  cacheLogo: string | null = null;
  defaultLG: string = 'assets/images/OpenGov-Logo.svg';
  defaultAppBuilderLogo: string = 'assets/images/gab-sidebar-logo.png';
  defaultSM: string = 'assets/images/OpenGov-Wand.svg';
  defaultIgnatiusLG: string = 'assets/images/markIgnatius-logo.svg';
  defaultIgnatiusSM: string = 'assets/images/logo.svg';
  themeRun: boolean = false;
  styleId: string = '';
  themes: any;
  isSetThemeCalled: boolean = false;
  private detailSubject = new Subject();
  companyId: number | null = null;
  subsink: SubSink = new SubSink();
  isThemeUpdated: boolean = false;
  isHelpDeskWidgetActive: boolean = true;
  isSidenavCollapsed: boolean = false;
  isSidenavTriggeredManually: boolean = false;
  isCustomUrl: boolean = false;
  applicationSidenavCollapsed: any = {};
  isUserMenuOpen: boolean = false;
  isHideLogoutInPspShellEnabled$!: Observable<LDFlagValue>;
  isHideLogoutInPspShellEnabled: boolean = false;
  showAppLibraryInSidebar$ = this.applicationService.showAppLibraryInSidebar$;

  private readonly navigationItemOrder: { [key: string]: number } = {
    'Fields': 1,
    'Relationships': 2,
    'Reports': 3,
    'Forms': 4,
    'Automations': 5,
    'Imports': 8,
    'Settings': 9
  };

  constructor(
    public renderer: Renderer2,
    private readonly store: Store,
    private readonly route: RouteService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly storage: StorageService,
    private readonly commonService: CommonService,
    private readonly alert: AlertService,
    public fieldService: FieldService,
    private readonly cdr: ChangeDetectorRef,
    private readonly navigationService: NavigationService,
    private readonly companyService: CompanyService,
    private readonly ldService: LaunchDarklyService,
    private readonly applicationService: ApplicationService
  ) {}

  isCurrentUserProfilePage(path: string): boolean {
    const current = this.route.currentUrl || this.router.url;
    return current === path || current.startsWith(path + '/');
  }

  async ngOnInit() {
    this.isCustomUrl = this.route.isCustomUrl;
    this.toggleDesktop(false);
    this.commonService.setOpenGovTheme();
    await this.loadLogo();
    this.isBillingAdmin = this.storage.getIsBillindAdmin() == 'true';
    this.isSuperAdmin = this.storage.getUserRole() == 'super_admin';
    this.isAdmin = this.storage.getUserRole() == 'admin';
    this.checkAppTheme();
    this.subsink.add(
      this.route.change().subscribe(async () => {
        this.checkAppTheme();
        await this.loadLogo();
        if (!this.route.getApplicationKey()) {
          this.commonService.setOpenGovTheme();
          if (this.route.currentUrl.endsWith('/apps')) {
            this.navigationService.currentAppName = 'App Builder';
            this.navigationService.showGlobalNav.emit('show');
          }
        }
      }),
      this.commonService.updateApplication.subscribe((res) => {
        this.isThemeUpdated = true;
        this.toggleDesktop(false, this.commonService.updatedToggleCollapse);
        this.removeAppTheme();
      }),
      this.commonService.updateTheme.subscribe((res) => {
        if (this.logoSrc === this.defaultLG || this.logoSrc === this.defaultAppBuilderLogo || this.logoSrc === this.defaultIgnatiusLG) {
          this.logoSrc = this.defaultLogoLarge;
          this.cdr.detectChanges();
        }
      }),
      this.authService.loginLogoutSubject.subscribe(() => {
        this.isImpersonate = this.storage.getImpersonateId();
      })
    );

    
    this.isHideLogoutInPspShellEnabled$ = this.ldService.getFlagValue(
      FEATURE_FLAGS.HIDE_LOGOUT_IN_PSP_SHELL,
      false
    );
    this.subsink.add(
      this.isHideLogoutInPspShellEnabled$.subscribe((value) => {
        this.isHideLogoutInPspShellEnabled = !!value && this.isInIframe();
      })
    );

    this.loadSubscribers();
    this.isPortal = this.storage.getPortalId() === null ? true : false;
    this.loadEntityFriendlyId();
  }

  private lastProcessedCompanyId: number | null = null;
  private readonly companyCache: Map<number, string | null> = new Map();
  private loadEntityFriendlyId() {
    this.subsink.add(
      this.route.change().pipe(
        map(() => this.route.getApplicationKey()),
        distinctUntilChanged(),
        debounceTime(200),
        switchMap(appKey => {
          if (appKey) {
            return this.store.pipe(
              select(getApplicationById),
              filter((data) => !!data && !!data[appKey]),
              map((data) => data[appKey]),
              filter((app) => !!app?.CompanyId),
              distinctUntilChanged((prev, curr) => prev?.CompanyId === curr?.CompanyId),
              take(1),
              switchMap((app) => {
                const companyId = app.CompanyId;
                if (this.companyCache.has(companyId)) {
                  const cachedId = this.companyCache.get(companyId);
                  return of(cachedId);
                }
                if (this.lastProcessedCompanyId === companyId) {
                  return EMPTY;
                }
                this.lastProcessedCompanyId = companyId;
                return this.companyService.getCompany(companyId).pipe(
                  take(1),
                  map(company => {
                    const entityFriendlyId = company?.EntityFriendlyId || null;
                    this.companyCache.set(companyId, entityFriendlyId);
                    this.lastProcessedCompanyId = null;
                    return entityFriendlyId;
                  })
                );
              })
            );
          }
          this.lastProcessedCompanyId = null;
          return of(null);
        }),
        distinctUntilChanged()
      ).subscribe(entityFriendlyId => {
        this.navigationService.setEntityFriendlyId(entityFriendlyId ?? null);
      })
    );
  }

  get isOpenGovTheme() {
    return !this.commonService.isIgnatiusThemeApplied;
  }

  get isAppNameVisible(): boolean {
    return this.isOpenGovTheme && !this.sidebarDesktop;
  }
  get isInApplication(): boolean {
    return !!this.route.getApplicationKey();
  }
  loadDefaultTablePage(params: any) {
    this.tableId = params?.LevelIds?.TableId;
    this.store.dispatch(new LoadDefaultPageForTable(this.tableId));
  }

  get defaultLogoLarge() {
    if (this.commonService.isIgnatiusThemeApplied) return this.defaultIgnatiusLG;
    return this.route.currentUrl.endsWith('apps')
      ? this.defaultAppBuilderLogo
      : this.defaultLG;
  }

  toggleDesktop = (isTriggerredManually: boolean = false, isCollapsed: boolean = false) => {
    const navEl = document.getElementById('side-navigation');
    this.isSidenavTriggeredManually = isTriggerredManually;
    this.sidebarDesktop = isTriggerredManually ? !this.sidebarDesktop : isCollapsed;
    // Force style/layout flush so width transition starts promptly on heavy nav trees.
    void navEl?.offsetWidth;
    if (this.sidebarDesktop) {
      this.renderer.addClass(document.body, 'mini-navbar');
    } else {
      this.renderer.removeClass(document.body, 'mini-navbar');
    }
    this.toggleEvent.emit(this.sidebarDesktop);
    requestAnimationFrame(() => {
      this.commonService.toggleSideBar.emit('toggle');
    });
  };

  toggleMobile = () => (this.sidebarMobile = !this.sidebarMobile);

  toggleUserMenu(): void {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  toggleUserMenuWithFocus(): void {
    const wasOpen = this.isUserMenuOpen;
    this.toggleUserMenu();
    if (wasOpen) {
      setTimeout(() => {
        const toggle = document.getElementById('user-menu-toggle');
        toggle?.focus();
      }, 0);
    } else if (this.isUserMenuOpen) {
      setTimeout(() => {
        const panel = document.getElementById('user-profile-panel');
        const firstLink = panel?.querySelector<HTMLElement>('a.new-sub-link');
        firstLink?.focus();
      }, 0);
    }
  }

  getRouterLink = (item: Item) => {
    const link = item.Href.replace('/#!/', '').split('/');
    if (link[1] === 'viewdatatable' && item.Title.startsWith('View')) {
      return [
        '/apps', link[2], 'tables', link[3], link[0] + 's', 'datatable', 'view', link[4],
      ].join('/');
    } else if (String(item.Title).startsWith('Add')) {
      return [
        '/apps', link[2], 'tables', link[3], link[0] + 's', 'datatable', 'view', link[4], 'form', link[5], 'add',
      ].join('/');
    }
    return [
      '/apps', link[1], 'tables', link[2], (link[0] === 'tablesettings' ? 'setting' : link[0]) + 's',
    ].join('/');
  };

  logout = () => this.authService.logout(true, true, this.storage.getPortalId());

  loadLogo = async () => {
    if (this.route.currentRoute().endsWith('/apps')) {
      this.storage.clearLogo();
      this.storage.clearIconData();
      this.icon = '';
      this.iconColor = '';
      this.logoSrc = this.defaultLogoLarge;
      this.isSidenavTriggeredManually = false;
      this.routeLink = '/apps';
    } else {
      this.logoSrc = this.storage.getLogo() ? this.storage.getLogo() : this.defaultLogoLarge;
      this.icon = this.storage.getIconData()?.name;
      this.iconColor = this.storage.getIconData()?.color;
    }
    this.cacheLogo = await this.storage.getLogo();
    this.subsink.add(
      this.store
        .pipe(
          select(getApplicationById),
          filter((data) => !!data[this.route.getApplicationKey()]),
          map((data) => data[this.route.getApplicationKey()])
        )
        .subscribe((data: any) => {
          if (Object.keys(data).length > 0) {
            if (data.ApplicationSetting && data.ApplicationSetting?.DisplayIgnatiusTheme) {
              this.commonService.setIgnatiusTheme();
            } else {
              this.commonService.setOpenGovTheme();
            }

            if (data.ApplicationSetting?.IsGlobalNavVisible) {
              this.navigationService.currentAppName = data.Name || 'App Builder';
              this.navigationService.showGlobalNav.emit('show');
            } else {
              this.navigationService.showGlobalNav.emit('hide');
            }
            this.isAuditLogEnable = data?.Audit;
            this.isNotificationLogEnable = data?.NotificationAudit;
            this.setSideNavMenu();
            if (data?.LogoData && !data.IsIcon) {
              if (this.cacheLogo == null || this.cacheLogo != data.LogoData) {
                this.logoSrc = data!.LogoData;
                this.storage.saveLogo(data!.LogoData);
              } else {
                this.logoSrc = this.cacheLogo!;
              }
              this.icon = '';
              this.iconColor = '';
              this.storage.clearIconData();
            } else if (data.Icon && data.IsIcon) {
              this.icon = data!.Icon;
              this.iconColor = data!.IconColor;
              this.storage.saveIconData({ name: this.icon, color: this.iconColor });
              this.storage.clearLogo();
            } else {
              this.logoSrc = this.defaultLogoLarge;
              this.icon = '';
              this.iconColor = '';
              this.storage.clearIconData();
              this.storage.clearLogo();
            }
            this.routeLink = `/apps/${data?.Key}`;
            this.appName = data.Name;
            if (!this.isSetThemeCalled && data.DefaultCssKey) {
              this.setAppTheme(data);
              this.isSetThemeCalled = true;
            }
            this.commonService.setGoogleTranslateLanguage(data?.ApplicationSetting?.Language || 'en');
            this.isHelpDeskWidgetActive = data?.ApplicationSetting?.IsHelpDeskWidgetActive;
            this.isSidenavCollapsed = data?.ApplicationSetting?.IsSideNavCollapsed;
            const sidenavCollapsed = Object.keys(this.applicationSidenavCollapsed).includes(this.route.getApplicationKey())
              ? this.applicationSidenavCollapsed[this.route.getApplicationKey()] || this.isSidenavCollapsed
              : this.isSidenavCollapsed;
            if (sidenavCollapsed && !this.isSidenavTriggeredManually) {
              this.toggleDesktop(false, true);
            }
          } else {
            this.logoSrc = this.defaultLogoLarge;
            this.storage.clearLogo();
            this.icon = '';
            this.iconColor = '';
            this.storage.clearIconData();
            this.removeAppTheme();
          }
        })
    );
  };

  loadSubscribers = () => {
    this.setSideNavMenu();
    this.subsink.add(
      this.store.pipe(select(getDefaultPageForTable), filter((data: any) => !!data[this.tableId])).subscribe((data: any) => {
        if (data[this.tableId]?.PageKey && data[this.tableId]?.PageKey) {
          this.router.navigate([`apps/${this.route.getApplicationKey()}`], {
            queryParams: { qP: data[this.tableId]?.PageKey },
          });
        }
      }),
      this.store
        .pipe(
          select(getApplicationData),
          filter((data) => !!data[this.route.getApplicationKey()])
        )
        .subscribe((data) => {
          this.applicationData = data[this.route.getApplicationKey()];
          this.setSideNavMenu();
        }),
      this.store.pipe(select(getUserProfile)).subscribe(profile => {
        this.companyId = profile?.CompanyId || null;
        profile?.ApplicationRoles?.forEach((role: any) => {
          this.applicationSidenavCollapsed[role.ApplicationKey] = role.IsSideNavCollapsed;
          if (role.ApplicationKey === this.route.getApplicationKey() && role.IsSideNavCollapsed) {
            this.toggleDesktop(false, role.IsSideNavCollapsed ? true : false);
          }
        });
      })
    );

    if (history.length && this.route.getApplicationKey()) {
      this.store.dispatch(new LoadNavigation(this.route.getApplicationKey()));
    }
    this.subsink.add(
      this.route.change().subscribe((data: any) => {
        setTimeout(() => this.commonService.scrollToTop(), 500);
        const urlNoQuery = (data?.url ?? '').split('?')[0];
        let rawUrl = urlNoQuery;
        while (rawUrl.endsWith('/')) rawUrl = rawUrl.slice(0, -1);
        const normalizedUrl = rawUrl.length ? rawUrl : '/';
        const appid = +data.url.split('/').filter((e: any) => e).slice(0, 2)[1];
        if (normalizedUrl === '/' || normalizedUrl === '/apps') {
          this.logoSrc = this.defaultLogoLarge;
          this.icon = '';
          this.iconColor = '';
          this.routeLink = this.route.getApplicationKey() ? `/apps/${this.route.getApplicationKey()}` : '/apps';
          this.appName = 'App Builder';
          this.navigationService.currentAppName = 'App Builder';
          this.storage.clearLogo();
          this.storage.clearIconData();
        }
        if (!appid) {
          this.commonService.setGoogleTranslateLanguage('en');
          this.toggleDesktop(false, false);
        }
        this.dispatchSelector(data.url);
      })
    );
  };

  setSideNavMenu = () => {
    this.subsink.add(
      this.store
        .pipe(select(getNavigation), filter((data) => !!data[this.route.getApplicationKey()]))
        .subscribe((data) => {
          if (!this.isAuditLogEnable && !this.isNotificationLogEnable) {
            this.navigation = data[this.route.getApplicationKey()].filter(
              (table) => this.excludeApplicationTitles.indexOf(table.Title) === -1
            );
          } else {
            this.navigation = data[this.route.getApplicationKey()];
            const tempObj: any[] = [];
            this.navigation.forEach((ele) => {
              if (ele?.Title != 'Audit Log' && ele?.Title != 'Notification Audit Log') {
                tempObj.push(ele);
              }
            });
            if (this.isAuditLogEnable) {
              const index = this.navigation.findIndex((item) => item.Title == 'Audit Log');
              if (!isNullOrUndefined(this.navigation[index])) {
                tempObj.push({
                  Order: this.navigation[index]?.Order,
                  Title: this.navigation[index]?.Title,
                  SingularReference: this.navigation[index].SingularReference,
                  LevelIds: this.navigation[index]?.LevelIds,
                  Href: this.navigation[index]?.Href,
                  Icon: this.navigation[index]?.Icon,
                  IsExpanded: this.navigation[index]?.IsExpanded,
                  Items: this.navigation[index]?.Items.filter(
                    (item: any) => item?.Title == 'View Table' || item?.Title == 'Reports'
                  ),
                });
              }
            }
            if (this.isNotificationLogEnable) {
              const n_index = this.navigation.findIndex((item) => item.Title == 'Notification Audit Log');
              if (!isNullOrUndefined(this.navigation[n_index])) {
                tempObj.push({
                  Order: this.navigation[n_index]?.Order,
                  Title: this.navigation[n_index]?.Title,
                  SingularReference: this.navigation[n_index].SingularReference,
                  LevelIds: this.navigation[n_index]?.LevelIds,
                  Href: this.navigation[n_index]?.Href,
                  Icon: this.navigation[n_index]?.Icon,
                  IsExpanded: this.navigation[n_index]?.IsExpanded,
                  Items: this.navigation[n_index]?.Items.filter(
                    (item: any) => item?.Title == 'View Table' || item?.Title == 'Reports'
                  ),
                });
              }
            }
            this.navigation = JSON.parse(JSON.stringify(tempObj));
          }
          this.navigation = this.sortNavigationItems(this.navigation);
          this.commonService.setGoogleTranslateLanguage(this.storage.getAppLanguage() || 'en');
        })
    );
  };

  dispatchSelector = (URL: string) => {
    if (['/', '/user/profile', '/apps'].indexOf(URL) > -1) {
      this.navigation = [];
      this.applicationData = <ApplicationData>{};
      this.storage.clearLogo();
    } else if (this.route.getApplicationKey() && !this.route.currentRoute().includes('redirectTo')) {
      this.store.dispatch(new LoadNavigation(this.route.getApplicationKey()));
    }
  };

  fullName = () => {
    let name = `${this.authService.userProfile?.FirstName || ''} ${this.authService.userProfile?.LastName || ''}`;
    if (this.isImpersonate) name += ' [Impersonated]';
    return name;
  };

  email = () => this.authService.userProfile?.Email;

  restoreSuperAdmin = () => {
    const impersonatedId = this.storage.getImpersonateId() || '';
    if (impersonatedId) this.store.dispatch(new ReturnToSuperAdmin(impersonatedId));
  };

  getAppId = () => this.route.getApplicationKey();

  get isHelpWidgetShow(): boolean {
    const hasAppId = !!this.getAppId();
    if (this.route.isCustomUrl) return hasAppId && this.isHelpDeskWidgetActive;
    return !hasAppId || this.isHelpDeskWidgetActive;
  }

  executeTodayFormulas = async () => {
    this.alert
      .fire(<SweetAlertOptions>{
        ...alertPresets.delete,
        title: 'Execute today formulas',
        text: 'Are you sure?',
        confirmButtonText: 'Yes',
      })
      .then(async (res) => {
        if (res.isConfirmed) {
          await this.fieldService.executeTodayFormulas().toPromise();
        }
      });
  };

  setAppTheme(data: any) {
    if (
      this.route.getApplicationKey() &&
      !this.themeRun &&
      data.DefaultCssKey &&
      !document.getElementById(`table_${this.route.getApplicationKey()}`)
    ) {
      this.subsink.sink = this.store
        .pipe(
          select(getThemeDetail),
          filter((theme) => !!theme[this.route.getApplicationKey()] && !!data.DefaultCssKey),
          map((theme) => theme[this.route.getApplicationKey()]),
          takeUntil(this.detailSubject)
        )
        .subscribe(theme => {
          if (!this.isThemeUpdated || !this.themes) {
            this.themes = theme[data.DefaultCssKey];
            this.addStyleOnHead(data);
          }
          this.isThemeUpdated = false;
        });
      if (this.route.getApplicationKey() && !document.getElementById(`table_${this.route.getApplicationKey()}`)) {
        this.store.dispatch(new LoadCSSTheme(this.route.getApplicationKey(), data.DefaultCssKey));
      }
    }
  }

  removeAppTheme() {
    const styleEl: any = document.getElementById(this.styleId);
    if (styleEl) document.head.removeChild(styleEl);
    this.themeRun = false;
    this.isSetThemeCalled = false;
    this.styleId = '';
    if (!this.isThemeUpdated) this.themes = '';
    this.detailSubject = new Subject();
  }

  checkAppTheme() {
    const styleEl: any = document.getElementById(this.styleId);
    let appId = '';
    if (this.styleId) appId = this.styleId.split('_')[1];
    if ((!this.route.getApplicationKey() || (appId && appId !== this.route.getApplicationKey())) && styleEl) {
      this.removeAppTheme();
    }
    const styles = document.querySelectorAll('style[id^="table_"]');
    if (styles.length) {
      if (!this.route.getApplicationKey()) {
        styles.forEach(style => style.remove());
        this.themeRun = false;
        this.isSetThemeCalled = false;
        this.styleId = '';
        this.themes = '';
        this.detailSubject = new Subject();
      } else {
        styles.forEach(style => {
          if (style.id !== `table_${this.route.getApplicationKey()}`) style.remove();
        });
      }
    }
  }

  addStyleOnHead(data: any) {
    if (data.IsDefaultCss && data.DefaultCssKey && !this.themeRun) {
      if (this.themes) {
        const style = document.createElement('style');
        style.id = `table_${this.route.getApplicationKey()}`;
        this.styleId = style.id;
        document.head.appendChild(style);
        const ast = Parser.parse(this.themes.Body);
        Parser.traverse(ast, node => {
          if (node && !node.parent.parent) {
            const rule = this.processNode(node);
            style.sheet?.insertRule(rule);
          }
        });
        this.themeRun = true;
        this.detailSubject.next();
        this.detailSubject.complete();
      }
    }
  }

  processNode(node: any) {
    if (node.body) {
      node.body = node.body.replace(/([^;]+?)(\s*!important)?(\s*;|\s*$)/g, (match: any, property: any, important: any, ending: any) => {
        return `${property}${important ? important : ' !important'}${ending}`;
      });
    }
    if (node.children && node.children.length > 0) {
      const childrenCss = node.children.map((childNode: any) => this.processNode(childNode)).join(' ');
      return `${node.selector} { ${childrenCss} }`;
    }
    return `${node.selector} { ${node.body} }`;
  }
    /**
   * Sort navigation items based on custom order mapping
   * @param navigations Array of navigation objects
   * @returns Sorted array of navigation objects with items reordered
   */
    private sortNavigationItems(navigations: Array<Navigation>): Array<Navigation> {
      return navigations.map(nav => ({
        ...nav,
        Items: [...nav.Items].sort((a, b) => {
          const orderA = this.getItemOrder(a.Title);
          const orderB = this.getItemOrder(b.Title);
          return orderA - orderB;
        })
      }));
    }
  
    /**
     * Get the display order for a navigation item by title
     * @param title The title of the navigation item
     * @returns The order number (defaults to 999 for unknown items)
     */
    private getItemOrder(title: string): number {
      // Check exact match first
      if (this.navigationItemOrder[title] !== undefined) {
        return this.navigationItemOrder[title];
      }
      
      // Handle dynamic "Add [Subject]" items (order 6)
      if (title.startsWith('Add ')) {
        return 6;
      }
      
      // Handle dynamic "View [Subjects]" items (order 7)
      if (title.startsWith('View ')) {
        return 7;
      }
      
      // Unknown items go to the end
      return 999;
    }
    skipToMain(event: MouseEvent) {
      event.preventDefault();
    
      const el = document.getElementById('main-content');
      if (el) {
        el.focus();              // works because you already have tabindex="-1"
        // optional: el.scrollIntoView({ block: 'start' });
      }
    }
  isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch (e) {
      // Cross-origin iframe - cannot access window.top
      // This exception itself indicates we're in an iframe
      console.debug('GAB is embedded in an iframe:', e);
      return true;
    }
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
    this.removeAppTheme();
    this.navigationService.showGlobalNav.emit('hide');
  }
}
